/**
 * WebPushService - Handles push notification subscriptions
 * Works for both web and Android PWA
 * 
 * NOTE: Chrome allows push notifications on localhost (HTTP) for development
 * Production requires HTTPS, but localhost works without HTTPS
 */
import { getApiUrl } from'./api';

const API_BASE_URL = getApiUrl();
const PUSH_API_URL =`${API_BASE_URL}/api/push`;

class WebPushService {
 constructor() {
 this.registration = null;
 this.publicKey = null;
 this.subscription = null;
 }

 /**
 * Ensure service worker is registered
 */
 async ensureServiceWorkerRegistered() {
 if (!('serviceWorker'in navigator)) {
 console.warn('⚠️ Service workers are not supported');
 return null;
 }
 
 if (this.registration) {
 return this.registration;
 }
 
 try {
 // Check for existing registration first to avoid conflicts
 let registration = await navigator.serviceWorker.getRegistration();
 
 if (registration) {
 console.log('✅ Using existing Service Worker registration');
 this.registration = registration;
 return this.registration;
 }
 
 // If no existing registration, wait for service worker to be ready
 // (it might be registering from index.js)
 try {
 registration = await navigator.serviceWorker.ready;
 if (registration) {
 console.log('✅ Service Worker ready, using existing registration');
 this.registration = registration;
 return this.registration;
 }
 } catch (e) {
 // Service worker not ready yet, try to register
 console.log('⚠️ Service Worker not ready, attempting registration...');
 }
 
 // Only register if no existing registration found
 if (!registration) {
 // Register service worker (without cache-busting to avoid conflicts)
 const swUrl =`/sw.js`;
 this.registration = await navigator.serviceWorker.register(swUrl, {
 scope:'/',
 updateViaCache:'none'});
 
 console.log('✅ Service Worker registered successfully');
 }
 
 // Wait for service worker to be ready
 await navigator.serviceWorker.ready;
 console.log('✅ Service Worker is ready');
 
 return this.registration;
 } catch (err) {
 console.error('❌ Service worker registration failed:', err);
 // Try to get existing registration as fallback
 try {
 const existingRegistration = await navigator.serviceWorker.getRegistration();
 if (existingRegistration) {
 this.registration = existingRegistration;
 return this.registration;
 }
 } catch (e) {
 // Ignore
 }
 return null;
 }
 }

 /**
 * Get VAPID public key from backend
 */
 async getVapidPublicKey() {
 if (this.publicKey !== null) {
 return this.publicKey;
 }
 
 try {
 const response = await fetch(`${PUSH_API_URL}/public-key`);
 const data = await response.json();
 
 if (data.success && data.publicKey) {
 this.publicKey = data.publicKey;
 console.log('✅ VAPID public key retrieved');
 return this.publicKey;
 } else {
 console.warn('⚠️ VAPID public key not available');
 return null;
 }
 } catch (err) {
 console.error('❌ Failed to fetch VAPID public key:', err);
 return null;
 }
 }

 /**
 * Convert base64 URL string to Uint8Array
 */
 urlBase64ToUint8Array(base64String) {
 const padding ='='.repeat((4 - (base64String.length % 4)) % 4);
 const base64 = (base64String + padding).replace(/-/g,'+').replace(/_/g,'/');
 const rawData = window.atob(base64);
 const outputArray = new Uint8Array(rawData.length);
 
 for (let i = 0; i < rawData.length; ++i) {
 outputArray[i] = rawData.charCodeAt(i);
 }
 
 return outputArray;
 }

 /**
 * Request notification permission
 */
 async requestPermission() {
 if (!('Notification'in window)) {
 console.warn('⚠️ Notifications are not supported');
 return'denied';
 }

 if (Notification.permission ==='granted') {
 console.log('✅ Notification permission already granted');
 return'granted';
 }

 if (Notification.permission ==='denied') {
 console.warn('⚠️ Notification permission denied');
 return'denied';
 }

 try {
 const permission = await Notification.requestPermission();
 console.log(`📱 Notification permission: ${permission}`);
 return permission;
 } catch (err) {
 console.error('❌ Error requesting notification permission:', err);
 return'denied';
 }
 }

 /**
 * Subscribe to push notifications
 */
 async subscribe() {
 try {
 console.log('📱 Starting push notification subscription...');
 
 // 1. Ensure service worker is registered and active
 const reg = await this.ensureServiceWorkerRegistered();
 if (!reg) {
 console.error('❌ Service worker not available');
 return { success: false, error:'Service worker not available'};
 }

 // Chrome requires service worker to be active before push subscription
 if (!reg.active) {
 console.warn('⚠️ Service worker not active, waiting for activation...');
 try {
 await new Promise((resolve, reject) => {
 const timeout = setTimeout(() => {
 reject(new Error('Service worker activation timeout'));
 }, 10000); // 10 second timeout

 const checkActive = () => {
 if (reg.active) {
 clearTimeout(timeout);
 resolve();
 } else {
 setTimeout(checkActive, 100);
 }
 };
 checkActive();
 });
 console.log('✅ Service worker is now active');
 } catch (timeoutError) {
 console.error('❌ Service worker activation timeout');
 return { success: false, error:'Service worker failed to activate. Please refresh the page.'};
 }
 }

 // 2. Get VAPID public key
 const vapidKey = await this.getVapidPublicKey();
 if (!vapidKey) {
 console.error('❌ VAPID public key not available');
 return { success: false, error:'VAPID key not available'};
 }

 // 3. Check notification permission (don't request if already granted/denied)
 const currentPermission = Notification.permission;
 
 if (currentPermission ==='default') {
 // Request permission only if not set
 const permission = await this.requestPermission();
 if (permission !=='granted') {
 console.error('❌ Notification permission not granted');
 return { success: false, error:'Permission not granted'};
 }
 } else if (currentPermission ==='denied') {
 console.error('❌ Notification permission denied');
 return { success: false, error:'Permission denied'};
 }
 // If permission is'granted', continue without requesting again

 // 4. Check if already subscribed
 const existingSubscription = await reg.pushManager.getSubscription();
 if (existingSubscription) {
 console.log('✅ Already subscribed to push notifications');
 console.log('📱 Existing subscription endpoint:', existingSubscription.endpoint.substring(0, 50) +'...');
 
 // Verify subscription keys are present (Chrome requirement)
 try {
 const subJson = existingSubscription.toJSON();
 if (!subJson.keys || !subJson.keys.p256dh || !subJson.keys.auth) {
 console.warn('⚠️ Existing subscription has invalid keys, re-subscribing...');
 // Unsubscribe invalid subscription
 await existingSubscription.unsubscribe();
 // Continue to create new subscription below
 } else {
 this.subscription = existingSubscription;
 
 // Always send existing subscription to backend (in case it wasn't saved before)
 // This ensures Chrome subscriptions are properly synced
 const backendResult = await this.sendSubscriptionToBackend(existingSubscription);
 if (backendResult.success) {
 console.log('✅ Existing subscription synced with backend');
 return { success: true, subscription: existingSubscription };
 } else {
 console.warn('⚠️ Failed to sync existing subscription with backend:', backendResult.error);
 // Don't fail - subscription exists, just backend sync failed
 // Return success but log the warning
 return { success: true, subscription: existingSubscription, warning:'Backend sync failed'};
 }
 }
 } catch (subError) {
 console.error('❌ Error validating existing subscription:', subError);
 // Try to unsubscribe and re-subscribe
 try {
 await existingSubscription.unsubscribe();
 console.log('🗑️ Unsubscribed invalid subscription, will create new one');
 } catch (unsubError) {
 console.error('❌ Failed to unsubscribe:', unsubError);
 }
 // Continue to create new subscription
 }
 }

 // 5. Subscribe to push manager
 console.log('📱 Subscribing to push manager...');
 console.log('📱 VAPID key length:', vapidKey.length);
 console.log('📱 Service worker active:', reg.active ?'Yes':'No');
 console.log('📱 Push manager supported:','pushManager'in reg ?'Yes':'No');
 
 // Check if push is supported
 if (!('pushManager'in reg)) {
 console.error('❌ PushManager not supported in this browser');
 return { success: false, error:'PushManager not supported'};
 }

 let subscription;
 try {
 // Chrome requires service worker to be active - double check
 if (!reg.active) {
 console.warn('⚠️ Service worker not active before subscription, waiting...');
 await new Promise((resolve) => {
 const checkActive = () => {
 if (reg.active) {
 resolve();
 } else {
 setTimeout(checkActive, 100);
 }
 };
 checkActive();
 });
 }
 
 // Convert VAPID key
 const applicationServerKey = this.urlBase64ToUint8Array(vapidKey);
 console.log('📱 VAPID key converted, length:', applicationServerKey.length);
 
 subscription = await reg.pushManager.subscribe({
 userVisibleOnly: true,
 applicationServerKey: applicationServerKey,
 });
 
 console.log('✅ Push subscription created successfully');
 console.log('📱 Subscription endpoint:', subscription.endpoint.substring(0, 50) +'...');
 console.log('📱 Subscription keys present:', {
 p256dh: !!subscription.getKey('p256dh'),
 auth: !!subscription.getKey('auth')
 });
 
 // Verify subscription is valid
 const subscriptionJson = subscription.toJSON();
 if (!subscriptionJson.keys || !subscriptionJson.keys.p256dh || !subscriptionJson.keys.auth) {
 throw new Error('Invalid subscription: missing encryption keys');
 }
 
 } catch (subscribeError) {
 console.error('❌ Push subscription error:', subscribeError);
 console.error('❌ Error name:', subscribeError.name);
 console.error('❌ Error message:', subscribeError.message);
 console.error('❌ Error stack:', subscribeError.stack);
 
 // Chrome-specific error handling
 if (subscribeError.name ==='NotAllowedError') {
 return { success: false, error:'Notification permission denied. Please allow notifications in browser settings.'};
 } else if (subscribeError.name ==='InvalidStateError') {
 return { success: false, error:'Service worker is not active. Please refresh the page and try again.'};
 } else if (subscribeError.name ==='AbortError') {
 return { success: false, error:'Subscription was aborted. Please try again.'};
 } else if (subscribeError.name ==='TypeError'&& subscribeError.message.includes('key')) {
 return { success: false, error:'Invalid VAPID key format. Please check server configuration.'};
 }
 
 return { success: false, error:`Subscription failed: ${subscribeError.message}`};
 }

 this.subscription = subscription;

 // 6. Send subscription to backend
 const backendResult = await this.sendSubscriptionToBackend(subscription);
 
 if (backendResult.success) {
 console.log('✅ Push notification subscription completed successfully');
 return { success: true, subscription };
 } else {
 console.error('❌ Failed to save subscription to backend');
 return { success: false, error: backendResult.error };
 }
 } catch (err) {
 console.error('❌ Push subscribe error:', err);
 return { success: false, error: err.message };
 }
 }

 /**
 * Send subscription to backend
 */
 async sendSubscriptionToBackend(subscription) {
 try {
 const token = localStorage.getItem('uttaapp_token');
 if (!token) {
 console.warn('⚠️ No auth token found, skipping backend subscription');
 return { success: false, error:'No auth token'};
 }

 const user = localStorage.getItem('user') 
 ? JSON.parse(localStorage.getItem('user')) 
 : null;

 // Detect device type and browser
 let deviceType ='web';
 let browserName ='unknown';
 const userAgent = navigator.userAgent.toLowerCase();
 
 // Detect Android (Chrome on Android uses FCM endpoints for web push)
 if (subscription.endpoint.includes('fcm.googleapis.com') && 
 (subscription.endpoint.includes('android.googleapis.com') || userAgent.includes('android'))) {
 deviceType ='android';
 browserName ='android';
 }
 // Detect Firefox (Mozilla push service)
 else if (subscription.endpoint.includes('updates.push.services.mozilla.com')) {
 deviceType ='web';
 browserName ='firefox';
 }
 // Detect Edge (Windows Push Notification Service)
 else if (subscription.endpoint.includes('wns2-') || subscription.endpoint.includes('notify.windows.com')) {
 deviceType ='web';
 browserName ='edge';
 }
 // Detect Chrome/Edge on desktop (Chrome uses FCM endpoints for web push via VAPID)
 // Note: This is web-push with VAPID, not FCM SDK
 else if (subscription.endpoint.includes('fcm.googleapis.com')) {
 deviceType ='web';
 // Edge detection (must check before Chrome)
 if (userAgent.includes('edg/') || userAgent.includes('edgios') || userAgent.includes('edge/')) {
 browserName ='edge';
 } 
 // Chrome detection
 else if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
 browserName ='chrome';
 } 
 // Default to chrome for Chrome's web push endpoints
 else {
 browserName ='chrome';
 }
 }
 // Try to detect from user agent as fallback
 else {
 deviceType ='web';
 // Edge detection (must check before Chrome)
 if (userAgent.includes('edg/') || userAgent.includes('edgios') || userAgent.includes('edge/')) {
 browserName ='edge';
 } 
 // Firefox detection
 else if (userAgent.includes('firefox')) {
 browserName ='firefox';
 } 
 // Chrome detection
 else if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
 browserName ='chrome';
 } 
 // Safari detection
 else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
 browserName ='safari';
 } 
 // Default
 else {
 browserName ='unknown';
 }
 }
 
 console.log(`🌐 Detected browser: ${browserName}, device type: ${deviceType}, user agent: ${navigator.userAgent.substring(0, 50)}...`);

 // Get subscription keys
 const subscriptionJson = subscription.toJSON();
 if (!subscriptionJson.keys || !subscriptionJson.keys.p256dh || !subscriptionJson.keys.auth) {
 console.error('❌ Invalid subscription: missing encryption keys');
 return { success: false, error:'Invalid subscription: missing encryption keys'};
 }
 
 const payload = {
 endpoint: subscription.endpoint,
 keys: subscriptionJson.keys,
 deviceType: deviceType,
 browser: browserName,
 userAgent: navigator.userAgent,
 role: user?.isDriver ?'driver':'user',
 };
 
 console.log('📤 Sending subscription to backend:', {
 endpoint: subscription.endpoint.substring(0, 50) +'...',
 browser: browserName,
 deviceType: deviceType,
 hasKeys: !!(payload.keys.p256dh && payload.keys.auth)
 });
 
 const response = await fetch(`${PUSH_API_URL}/subscribe`, {
 method:'POST',
 headers: {'Content-Type':'application/json','Authorization':`Bearer ${token}`,
 },
 body: JSON.stringify(payload),
 });

 if (!response.ok) {
 const errorText = await response.text();
 console.error('❌ Backend subscription HTTP error:', response.status, errorText);
 return { success: false, error:`HTTP ${response.status}: ${errorText}`};
 }

 const data = await response.json();
 
 if (data.success) {
 console.log('✅ Subscription saved to backend successfully');
 return { success: true };
 } else {
 console.error('❌ Backend subscription failed:', data.message);
 console.error('❌ Backend error details:', data);
 return { success: false, error: data.message ||'Unknown error'};
 }
 } catch (err) {
 console.error('❌ Error sending subscription to backend:', err);
 return { success: false, error: err.message };
 }
 }

 /**
 * Unsubscribe from push notifications
 */
 async unsubscribe() {
 try {
 const reg = await this.ensureServiceWorkerRegistered();
 if (!reg) {
 return { success: false, error:'Service worker not available'};
 }

 const subscription = await reg.pushManager.getSubscription();
 if (!subscription) {
 console.log('ℹ️ No active subscription to unsubscribe');
 return { success: true };
 }

 // Unsubscribe from push manager
 await subscription.unsubscribe();
 console.log('✅ Unsubscribed from push manager');

 // Remove from backend
 const token = localStorage.getItem('uttaapp_token');
 if (token) {
 await fetch(`${PUSH_API_URL}/unsubscribe`, {
 method:'POST',
 headers: {'Content-Type':'application/json','Authorization':`Bearer ${token}`,
 },
 body: JSON.stringify({
 endpoint: subscription.endpoint,
 }),
 });
 console.log('✅ Removed subscription from backend');
 }

 this.subscription = null;
 return { success: true };
 } catch (err) {
 console.error('❌ Unsubscribe error:', err);
 return { success: false, error: err.message };
 }
 }

 /**
 * Check if push notifications are supported
 */
 isSupported() {
 return ('serviceWorker'in navigator &&'PushManager'in window &&'Notification'in window
 );
 }

 /**
 * Get current subscription status
 */
 async getSubscriptionStatus() {
 try {
 const reg = await this.ensureServiceWorkerRegistered();
 if (!reg) {
 return { subscribed: false, supported: false };
 }

 const subscription = await reg.pushManager.getSubscription();
 return {
 subscribed: !!subscription,
 supported: true,
 permission: Notification.permission,
 subscription: subscription ? subscription.toJSON() : null
 };
 } catch (err) {
 console.error('Error getting subscription status:', err);
 return { subscribed: false, supported: false, error: err.message };
 }
 }

 /**
 * Force re-subscribe (useful when notifications stop working)
 */
 async resubscribe() {
 try {
 console.log('🔄 Force re-subscribing to push notifications...');
 
 // 1. Unsubscribe first
 const reg = await this.ensureServiceWorkerRegistered();
 if (reg) {
 const existingSubscription = await reg.pushManager.getSubscription();
 if (existingSubscription) {
 console.log('🗑️ Unsubscribing from existing subscription...');
 await existingSubscription.unsubscribe();
 console.log('✅ Unsubscribed');
 }
 }
 
 // 2. Clear cached subscription
 this.subscription = null;
 this.publicKey = null;
 
 // 3. Wait a bit
 await new Promise(resolve => setTimeout(resolve, 500));
 
 // 4. Re-subscribe
 return await this.subscribe();
 } catch (err) {
 console.error('❌ Re-subscribe error:', err);
 return { success: false, error: err.message };
 }
 }

 /**
 * Verify subscription is still valid and synced with backend
 */
 async verifySubscription() {
 try {
 console.log('🔍 Verifying push subscription...');
 
 const reg = await this.ensureServiceWorkerRegistered();
 if (!reg) {
 return { valid: false, error:'Service worker not available'};
 }

 const subscription = await reg.pushManager.getSubscription();
 if (!subscription) {
 console.warn('⚠️ No subscription found');
 return { valid: false, error:'No subscription found'};
 }

 console.log('✅ Local subscription exists');
 
 // Verify subscription is synced with backend
 const backendResult = await this.sendSubscriptionToBackend(subscription);
 if (backendResult.success) {
 console.log('✅ Subscription verified and synced with backend');
 return { valid: true, synced: true };
 } else {
 console.warn('⚠️ Subscription exists locally but backend sync failed:', backendResult.error);
 return { valid: true, synced: false, error: backendResult.error };
 }
 } catch (err) {
 console.error('❌ Verify subscription error:', err);
 return { valid: false, error: err.message };
 }
 }
}

// Export singleton instance
const webPushService = new WebPushService();

// Add global helper for debugging (only in development)
if (typeof window !=='undefined'&& process.env.NODE_ENV !=='production') {
 window.debugPushNotifications = {
 status: () => webPushService.getSubscriptionStatus(),
 verify: () => webPushService.verifySubscription(),
 resubscribe: () => webPushService.resubscribe(),
 subscribe: () => webPushService.subscribe(),
 unsubscribe: () => webPushService.unsubscribe(),
 help: () => {
 console.log(`🔧 Push Notification Debug Helper:

window.debugPushNotifications.status() - Check subscription status
window.debugPushNotifications.verify() - Verify subscription is synced
window.debugPushNotifications.resubscribe() - Force re-subscribe (fix broken subscriptions)
window.debugPushNotifications.subscribe() - Subscribe to push notifications
window.debugPushNotifications.unsubscribe() - Unsubscribe from push notifications

Example:
 await window.debugPushNotifications.status()
 await window.debugPushNotifications.resubscribe()`);
 }
 };
 console.log('💡 Push notification debug helper available: window.debugPushNotifications.help()');
}

export default webPushService;

